/**
 * Magicks: fixed element sequences cast with Space, or G (DESIGN 2.9).
 *
 * The five added after v1 follow the grammar the first ten suggest: "X
 * shield X" puts a thing of X on the field (Tornado, Vortex, Summon
 * Elemental; Nullify and Time Warp already end in Shield), Thunder Storm
 * grows out of Rain's water and steam, and Meteor Shower is lava on both
 * sides of a rock. Every sequence is typeable (tests/shared/magicks.test.ts
 * runs the key presses through pushElement).
 */

import type { Element } from './elements.js';
import { PVP } from './constants.js';

export type MagickId = 'revive'|'haste'|'teleport'|'thunderbolt'|'rain'|'nullify'|'timewarp'|'conflagration'|'phoenix'|'blizzard'
  |'meteor'|'tornado'|'thunderstorm'|'vortex'|'elemental'|'stoptime';

export interface MagickDef { seq: Element[]; name: string; desc: string }

export const MAGICK_IDS: readonly MagickId[] = [
  'revive', 'haste', 'teleport', 'thunderbolt', 'rain', 'nullify', 'timewarp', 'conflagration', 'phoenix', 'blizzard',
  'meteor', 'tornado', 'thunderstorm', 'vortex', 'elemental', 'stoptime',
];

/** Sequence must equal the queue exactly, in order (DESIGN 2.9). */
export const MAGICKS: Readonly<Record<MagickId, MagickDef>> = {
  revive: {
    seq: ['life', 'lightning'],
    name: 'Revive',
    desc: 'Revives the dead ally nearest you at 100 HP, 4 u ahead.',
  },
  haste: {
    seq: ['lightning', 'arcane', 'fire'],
    name: 'Haste',
    desc: 'Hasted for 10 s: double move speed and beam turn rate.',
  },
  teleport: {
    seq: ['lightning', 'arcane', 'lightning'],
    name: 'Teleport',
    desc: 'Blink 8 u toward your aim. Works while frozen or knocked down.',
  },
  thunderbolt: {
    seq: ['steam', 'lightning', 'arcane', 'lightning'],
    name: 'Thunder Bolt',
    desc: '5000 lightning on the healthiest enemy near a point 8 u ahead; 500 splash within 1 u.',
  },
  rain: {
    seq: ['water', 'steam'],
    name: 'Rain',
    desc: '17.5 s of rain: everyone in the arena is wet every 2 s and burning is put out.',
  },
  nullify: {
    seq: ['arcane', 'shield'],
    name: 'Nullify',
    desc: 'Removes every mine, barrier, wall, storm, aura, armor, status, global magick and queue.',
  },
  timewarp: {
    seq: ['cold', 'shield'],
    name: 'Time Warp',
    desc: 'Slows every enemy and player for 15 s.',
  },
  conflagration: {
    seq: ['steam', 'fire', 'steam', 'fire', 'steam'],
    name: 'Conflagration',
    desc: '600 fire and 3 burn stacks to everyone within 8 u (not you).',
  },
  phoenix: {
    seq: ['life', 'lightning', 'fire'],
    name: 'Summon Phoenix',
    desc: '400 fire, knockdown and 2 burn stacks within 4 u (you included), then every dead player returns at full HP.',
  },
  blizzard: {
    seq: ['cold', 'ice', 'cold'],
    name: 'Blizzard',
    desc: '10 s blizzard: everyone chilled every 2 s (frozen if wet); the pond freezes over.',
  },
  meteor: {
    seq: ['lava', 'earth', 'lava'],
    name: 'Meteor Shower',
    desc: '7 burning rocks fall around a point 7 u ahead, each marked by its shadow first. They hit anyone, and frozen targets shatter.',
  },
  tornado: {
    seq: ['steam', 'shield', 'steam'],
    name: 'Tornado',
    desc: 'A twister walks your aim for 6 s, flooring and flinging whoever it touches (you too). Spray, beam or throw an element into it and it carries it.',
  },
  thunderstorm: {
    seq: ['steam', 'steam', 'lightning'],
    name: 'Thunder Storm',
    desc: 'A storm cloud 7 u ahead for 8 s: lightning strikes the enemies under it, wet ones first. The strikes splash anyone close.',
  },
  vortex: {
    seq: ['arcane', 'shield', 'arcane'],
    name: 'Vortex',
    desc: 'A vortex 7 u ahead pulls everything in for 3.5 s and swallows missiles, then bursts: the more it caught, the bigger the blast.',
  },
  elemental: {
    seq: ['earth', 'shield', 'earth'],
    name: 'Summon Elemental',
    desc: 'An earth elemental fights beside you for 18 s. Spray, beam or throw an element into it and its slams carry that element.',
  },
  // The one Magick with a woven piece in it (docs/spells-depth.md 4.1b): a Moment in front of Time Warp's keys.
  stoptime: {
    seq: ['moment', 'cold', 'shield'],
    name: 'Lesser Time Stop',
    desc: 'Every enemy runs at 15% for 3 s; you and your friends keep full speed. 30 s cooldown. Weave a Moment, then R E.',
  },
};

/** True when `seq` begins with every element of `queue`, in order. */
function startsWith(seq: readonly Element[], queue: readonly Element[]): boolean {
  if (queue.length > seq.length) return false;
  for (let i = 0; i < queue.length; i++) if (seq[i] !== queue[i]) return false;
  return true;
}

/**
 * Magicks whose pieces may come in any order: the Lesser Time Stop's Moment is often pulled out of the stash
 * after R E, so it lands last ([cold, shield, moment]); that is the same spell.
 */
const ANY_ORDER: readonly MagickId[] = ['stoptime'];

/** Same elements, same counts, any order. */
function samePieces(a: readonly Element[], b: readonly Element[]): boolean {
  if (a.length !== b.length) return false;
  const rest = [...b];
  for (const el of a) {
    const i = rest.indexOf(el);
    if (i < 0) return false;
    rest.splice(i, 1);
  }
  return true;
}

/** The magick whose sequence equals the queue exactly (order matters, except for ANY_ORDER), else null. */
export function matchMagick(queue: readonly Element[]): MagickId | null {
  for (const id of MAGICK_IDS) {
    const seq = MAGICKS[id].seq;
    if (seq.length === queue.length && startsWith(seq, queue)) return id;
  }
  for (const id of ANY_ORDER) if (samePieces(MAGICKS[id].seq, queue)) return id;
  return null;
}

/**
 * PvP: seconds of the one Magick cooldown a cast starts (docs/spellweave-modes.md §2). The server
 * sets it and the client predicts it, so both read it from here.
 */
export function pvpMagickCooldown(id: MagickId): number {
  if (id === 'stoptime') return PVP.stopCooldown;
  return id === 'haste' || id === 'teleport' ? PVP.quickCooldown : PVP.magickCooldown;
}

/**
 * Magicks whose sequence starts with the queue (for the HUD hint, DESIGN 6.5).
 * An exact match is included, so the hint keeps showing "Revive (G)" once the
 * queue is complete. Empty queue -> [].
 */
export function magickPrefix(queue: readonly Element[]): MagickId[] {
  if (queue.length === 0) return [];
  return MAGICK_IDS.filter((id) => startsWith(MAGICKS[id].seq, queue));
}
